package diana.soleil.design.iraniancanadienbazzar.feature.chat

import androidx.compose.foundation.background
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableLongStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppBottomSheet
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppButtonVariant
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppChip
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppIconButton
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPin
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.MapPoint
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.StatusBarScrim
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.component.AppMap
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import diana.soleil.design.iraniancanadienbazzar.core.model.MapPrecision
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.Res
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.inbox_back_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_approx_note
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_check_in
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_check_in_confirm
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_check_in_desc
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_check_in_failed
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_check_in_list_note
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_check_in_no_location
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_checked_in
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_leave
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_visibility_area
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_visibility_area_desc
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_visibility_city
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_visibility_city_desc
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_zoom_in
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_zoom_out
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_attribution
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_cluster_a11y
import diana.soleil.design.iraniancanadienbazzar.feature.chat.resources.people_map_pin_a11y
import kotlinx.coroutines.delay
import kotlin.time.Clock
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

/**
 * People map: pins for members who are CHECKED IN right now (approximate ~11 km / ~2 km areas — the repo
 * snaps coordinates at save; precise positions are never stored). Being on the map is a manual check-in
 * that lasts 4 hours (App Store 5.1.2) — there is no "always show me" setting. Pin tap → person card.
 */
@Composable
fun PeopleMapRoute(
    onBack: () -> Unit,
    onOpenConversation: (conversationId: String) -> Unit,
    onRequireSignIn: () -> Unit,
    onOpenPerson: (uid: String) -> Unit = {},
    modifier: Modifier = Modifier,
    viewModel: PeopleViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val checkIn by viewModel.checkIn.collectAsStateWithLifecycle()
    LaunchedEffect(state.signInRequired) { if (state.signInRequired) { viewModel.consumeGate(); onRequireSignIn() } }
    LaunchedEffect(state.openConversationId) {
        val id = state.openConversationId ?: return@LaunchedEffect
        viewModel.consumeOpen(); onOpenConversation(id)
    }
    LaunchedEffect(Unit) { viewModel.ensureLocation() }

    // Expiry is evaluated against a clock that ticks, so pins (and "time left") drop off while open.
    var nowMillis by remember { mutableLongStateOf(Clock.System.now().toEpochMilliseconds()) }
    LaunchedEffect(Unit) {
        while (true) {
            delay(CLOCK_TICK_MILLIS)
            nowMillis = Clock.System.now().toEpochMilliseconds()
        }
    }

    val mappable = state.people.filter { it.isOnMapAt(nowMillis) }
    val myExpiry = state.myCard?.takeIf { it.isOnMapAt(nowMillis) }?.mapCheckInExpiresAtMillis
    var selectedUid by remember { mutableStateOf<String?>(null) }
    var showCheckIn by remember { mutableStateOf(false) }
    val selected = mappable.firstOrNull { it.uid == selectedUid }
    // Camera: the user's own area when known, else the first mapped person, else metro fallback.
    val fallback = state.mapCenter
    var center by remember(fallback) {
        mutableStateOf(
            MapPoint(
                mappable.firstOrNull()?.approxLat ?: fallback.lat,
                mappable.firstOrNull()?.approxLng ?: fallback.lng,
            ),
        )
    }
    var zoom by remember { mutableStateOf(PEOPLE_MAP_ZOOM) }

    Box(modifier = modifier.fillMaxSize()) {
        val peopleClusterLabel = stringResource(Res.string.people_map_cluster_a11y)
        AppMap(
            center = center,
            zoom = zoom,
            onCameraChange = { c, z -> center = c; zoom = z },
            pins = mappable.map { person ->
                MapPin(
                    id = person.uid,
                    lat = person.approxLat!!,
                    lng = person.approxLng!!,
                    selected = person.uid == selectedUid,
                    // Avatar-chip marker: photo when they have one, else initial on per-id color.
                    label = person.displayName,
                    avatarUrl = person.photoUrl,
                )
            },
            onPinClick = { id -> selectedUid = if (selectedUid == id) null else id },
            attribution = stringResource(Res.string.people_map_attribution),
            pinContentDescription = stringResource(Res.string.people_map_pin_a11y),
            clusterContentDescription = { n -> peopleClusterLabel.replace("%1\u0024d", n.toString()) },
            userLocation = null, // deliberately not drawn — this surface shows AREAS, not positions
            radiusKm = null,
            radiusCenter = null,
            zoomInContentDescription = stringResource(Res.string.people_map_zoom_in),
            zoomOutContentDescription = stringResource(Res.string.people_map_zoom_out),
            modifier = Modifier.fillMaxSize(),
        )
        // Full-bleed map: without a scrim the system clock and battery draw straight onto street
        // labels. Same fix as the directory map.
        StatusBarScrim(scrolled = true)

        Box(
            modifier = Modifier
                .statusBarsPadding()
                .padding(AppTheme.spacing.md)
                .background(AppTheme.colors.surface, AppTheme.shapes.pill),
        ) {
            AppIconButton(
                iconName = "arrow_back",
                contentDescription = stringResource(Res.string.inbox_back_a11y),
                onClick = onBack,
            )
        }
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .align(Alignment.BottomCenter)
                .navigationBarsPadding()
                .padding(AppTheme.spacing.md),
            verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            if (selected != null) {
                PersonCard(
                    person = selected,
                    onConnect = { viewModel.connect(selected) },
                    onOpen = { onOpenPerson(selected.uid) },
                )
            }
            CheckInPanel(
                expiresAtMillis = myExpiry,
                nowMillis = nowMillis,
                state = checkIn,
                onCheckIn = { showCheckIn = true },
                onLeave = viewModel::leaveMap,
            )
            Text(
                text = stringResource(Res.string.people_map_approx_note),
                style = AppTheme.typography.labelSm,
                color = AppTheme.colors.onSurfaceVariant,
                modifier = Modifier
                    .background(AppTheme.colors.surface, AppTheme.shapes.pill)
                    .padding(horizontal = AppTheme.spacing.md, vertical = AppTheme.spacing.xs),
            )
        }
    }

    if (showCheckIn) {
        CheckInSheet(
            alreadyListed = state.myCard?.discoverable == true,
            onCheckIn = { precision ->
                viewModel.checkInOnMap(precision)
                showCheckIn = false
            },
            onDismiss = { showCheckIn = false },
        )
    }
}

/** My check-in status: time left + Leave, or the Check in button (plus the last error, if any). */
@Composable
private fun CheckInPanel(
    expiresAtMillis: Long?,
    nowMillis: Long,
    state: MapCheckInUiState,
    onCheckIn: () -> Unit,
    onLeave: () -> Unit,
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .background(AppTheme.colors.surface, AppTheme.shapes.card)
            .padding(AppTheme.spacing.md),
        verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
    ) {
        if (expiresAtMillis != null) {
            val minutesLeft = ((expiresAtMillis - nowMillis) / 60_000L).coerceAtLeast(1L)
            Text(
                text = stringResource(
                    Res.string.people_map_checked_in,
                    (minutesLeft / 60).toInt(),
                    (minutesLeft % 60).toInt(),
                ),
                style = AppTheme.typography.bodyMd,
                color = AppTheme.colors.onSurface,
            )
            AppButton(
                text = stringResource(Res.string.people_map_leave),
                onClick = onLeave,
                variant = AppButtonVariant.Secondary,
                loading = state.busy,
                modifier = Modifier.fillMaxWidth(),
            )
        } else {
            AppButton(
                text = stringResource(Res.string.people_map_check_in),
                onClick = onCheckIn,
                leadingIcon = "map",
                loading = state.busy,
                modifier = Modifier.fillMaxWidth(),
            )
        }
        state.error?.let { error ->
            Text(
                text = stringResource(
                    when (error) {
                        MapCheckInError.NoLocation -> Res.string.people_map_check_in_no_location
                        MapCheckInError.Failed -> Res.string.people_map_check_in_failed
                    },
                ),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.error,
            )
        }
    }
}

/** One manual check-in: pick an approximate area, confirm, and the pin lasts 4 hours. */
@Composable
private fun CheckInSheet(
    alreadyListed: Boolean,
    onCheckIn: (MapPrecision) -> Unit,
    onDismiss: () -> Unit,
) {
    // City area is the more private level, so it is the starting choice.
    var precision by remember { mutableStateOf(MapPrecision.City) }
    AppBottomSheet(onDismissRequest = onDismiss, title = stringResource(Res.string.people_map_check_in)) {
        Text(
            text = stringResource(Res.string.people_map_check_in_desc),
            style = AppTheme.typography.bodyMd,
            color = AppTheme.colors.onSurfaceVariant,
        )
        Row(
            modifier = Modifier
                .horizontalScroll(rememberScrollState())
                .padding(top = AppTheme.spacing.sm),
            horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
        ) {
            CHECK_IN_LEVELS.forEach { option ->
                AppChip(
                    label = stringResource(option.labelRes()),
                    selected = precision == option,
                    onClick = { precision = option },
                )
            }
        }
        Text(
            text = stringResource(precision.descriptionRes()),
            style = AppTheme.typography.bodySm,
            color = AppTheme.colors.onSurfaceVariant,
        )
        if (!alreadyListed) {
            Text(
                text = stringResource(Res.string.people_map_check_in_list_note),
                style = AppTheme.typography.bodySm,
                color = AppTheme.colors.onSurfaceVariant,
            )
        }
        AppButton(
            text = stringResource(Res.string.people_map_check_in_confirm),
            onClick = { onCheckIn(precision) },
            modifier = Modifier.fillMaxWidth().padding(top = AppTheme.spacing.lg),
        )
    }
}

private val CHECK_IN_LEVELS = listOf(MapPrecision.City, MapPrecision.Neighbourhood)

private fun MapPrecision.labelRes() = when (this) {
    MapPrecision.Neighbourhood -> Res.string.people_map_visibility_area
    else -> Res.string.people_map_visibility_city
}

private fun MapPrecision.descriptionRes() = when (this) {
    MapPrecision.Neighbourhood -> Res.string.people_map_visibility_area_desc
    else -> Res.string.people_map_visibility_city_desc
}

private const val PEOPLE_MAP_ZOOM = 11.0
private const val CLOCK_TICK_MILLIS = 30_000L
